// Stage 3C-map — geographic at-a-glance for the live STV count.
//
// Reuses the existing 13-district Malta geometry (MAP_DATA from
// buildMapPaths). For each district, the polygon interior is split
// into 5 horizontal bands clipped to the district outline — one band
// per seat. Bands default to a neutral grey ("seat not yet decided")
// and fill bottom-up in chronological election order with the party
// colour of each elected candidate.
//
// A district sweeping 5-0 reads as five same-colour bands. A 3 PL /
// 2 PN district reads as three red bands at the bottom (the first
// three elected, in their election order) topped by two blue. The
// "fills up" metaphor mirrors the count's own progress.
//
// Driven entirely by useResultsData (real DB-backed state OR demo,
// per the results_demo_mode flag) + useResultsRoster (party lookup
// by candidate id). Geometry NEVER rebuilt — the same MAP_DATA the
// homepage MapModule consumes today.

import { useMemo, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { MAP_DATA } from '../../lib/buildMapPaths';
import {
  SEATS_PER_DISTRICT,
  deriveDistrictCount,
} from '../../data/electionday-results-types';
import { useResultsData } from './useResultsData';
import {
  partyColour,
  partyChip,
  useResultsRoster,
  type RosterCandidate,
} from './useResultsRoster';
import './ResultsMap.css';
import './Results.css'; // for .results-demo-inline

/** Unfilled-seat fill. A near-neutral grey that reads as "empty" but
 *  still discernible against the white card background and the dark
 *  district stroke. */
const EMPTY_FILL = '#dcdcdc';

interface Props {
  /** Visible card title — caller can vary it ("National map" on /results,
   *  "Live results map" on the homepage). Defaults to a neutral title. */
  title?: string;
  /** Visual variant.
   *  - `normal` (default) — white card, drop-shadow tooltip, legend.
   *  - `chroma` — chromakey-green background (#00B140), no card chrome,
   *    no tooltip / legend / title. Used by /andrea_map as a clean
   *    overlay broadcasters can key out in their switcher. */
  variant?: 'normal' | 'chroma';
}

export default function ResultsMap({ title = 'Results map', variant = 'normal' }: Props) {
  const { data, isDemo } = useResultsData();
  const { byDistrict, byId } = useResultsRoster();
  // Hover state for the tooltip — viewport coordinates so the
  // tooltip can be rendered as a fixed-position div outside the SVG
  // (cleanest way to overlay text on top of SVG geometry without
  // foreignObject-style positioning quirks).
  const [hover, setHover] = useState<{
    districtId: number;
    x: number;
    y: number;
  } | null>(null);

  // For each district id, derive the 5-element fill array (bottom-up
  // by election order). All work memoised on the upstream state hash
  // so panning round the page doesn't re-derive.
  const fillsByDistrict = useMemo(() => {
    const out: Record<number, string[]> = {};
    for (const p of MAP_DATA.paths) {
      const ids = byDistrict(p.id).map((c) => c.id);
      const derived = deriveDistrictCount(p.id, data.districts[String(p.id)], ids);
      const seatColours: string[] = [];
      for (let i = 0; i < SEATS_PER_DISTRICT; i++) {
        const electedId = derived.electedSeats[i];
        if (!electedId) {
          seatColours.push(EMPTY_FILL);
          continue;
        }
        const cand = byId(electedId);
        seatColours.push(cand ? partyColour(cand.party) : EMPTY_FILL);
      }
      out[p.id] = seatColours;
    }
    return out;
  }, [data, byDistrict, byId]);

  // Legend palette pulled from candidates actually elected somewhere —
  // keeps PL/PN front-and-centre when the result is two-horse, surfaces
  // smaller parties only if they actually won a seat.
  const legend = useMemo(() => {
    const seen = new Map<string, RosterCandidate>();
    for (const p of MAP_DATA.paths) {
      const ids = byDistrict(p.id).map((c) => c.id);
      const derived = deriveDistrictCount(p.id, data.districts[String(p.id)], ids);
      for (const eid of derived.electedSeats) {
        const cand = byId(eid);
        if (cand && !seen.has(cand.party)) seen.set(cand.party, cand);
      }
    }
    return Array.from(seen.values());
  }, [data, byDistrict, byId]);

  const isChroma = variant === 'chroma';
  return (
    <section
      className={`results-map${isChroma ? ' results-map--chroma' : ''}`}
      aria-label={title}
    >
      {!isChroma && (
        <div className="results-map__head">
          <div className="results-map__head-left">
            {isDemo && <p className="results-demo-inline">⚠ Demo / Test Data</p>}
            <p className="results-card__kicker">National view</p>
            <h3 className="results-card__title">{title}</h3>
          </div>
        </div>
      )}

      <div className="results-map__svg-wrap" style={{ position: 'relative' }}>
        <svg
          viewBox={MAP_DATA.viewBox}
          className="results-map__svg"
          role="img"
          aria-label="Malta electoral districts, live seat fill by party"
        >
          {/* One clipPath per district — the district outline. The
              band rects below are clipped to this so they only paint
              inside the district shape. */}
          <defs>
            {MAP_DATA.paths.map((p) => (
              <clipPath id={`rmap-clip-${p.id}`} key={`clip-${p.id}`}>
                <path d={p.path} />
              </clipPath>
            ))}
          </defs>

          {/* Bands — 5 per district, bottom-up chronological. Each
              district group hosts the bands + the 4 seat dividers +
              an invisible hit-rect that captures pointer events so
              hovering anywhere on the district lights up the
              tooltip. */}
          {MAP_DATA.paths.map((p) => {
            const colours = fillsByDistrict[p.id] ?? [];
            const { x, y, width, height } = p.bbox;
            const bandH = height / SEATS_PER_DISTRICT;
            // Render five horizontal bands. Band index 0 is the first-
            // elected seat → bottom band (y = bbox.bottom - bandH).
            const bands = [];
            for (let i = 0; i < SEATS_PER_DISTRICT; i++) {
              const bandY = y + (SEATS_PER_DISTRICT - 1 - i) * bandH;
              bands.push(
                <rect
                  key={`b${p.id}-${i}`}
                  x={x}
                  y={bandY}
                  width={width}
                  height={bandH}
                  fill={colours[i] ?? EMPTY_FILL}
                  clipPath={`url(#rmap-clip-${p.id})`}
                />,
              );
            }
            // 4 dividers between the 5 seat bands, drawn after the
            // bands so they sit on top. White at 70% opacity reads
            // against any party colour AND against the empty grey.
            const dividers = [];
            for (let j = 0; j < SEATS_PER_DISTRICT - 1; j++) {
              const dividerY = y + (j + 1) * bandH;
              dividers.push(
                <line
                  key={`div${p.id}-${j}`}
                  x1={x}
                  x2={x + width}
                  y1={dividerY}
                  y2={dividerY}
                  className="results-map__seat-divider"
                  clipPath={`url(#rmap-clip-${p.id})`}
                />,
              );
            }
            // Invisible pointer-event target sized to the bbox + clipped
            // to the district outline → hover only fires when the cursor
            // is actually inside the district polygon. Mouse-move keeps
            // the tooltip glued to the cursor. Skipped entirely in
            // chroma mode — broadcasters don't hover their overlay.
            const onEnter = (e: ReactMouseEvent<SVGRectElement>) => {
              setHover({ districtId: p.id, x: e.clientX, y: e.clientY });
            };
            const onMove = (e: ReactMouseEvent<SVGRectElement>) => {
              setHover({ districtId: p.id, x: e.clientX, y: e.clientY });
            };
            const onLeave = () => setHover(null);
            return (
              <g key={`bands-${p.id}`}>
                {bands}
                {dividers}
                {!isChroma && (
                  <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    fill="transparent"
                    clipPath={`url(#rmap-clip-${p.id})`}
                    onMouseEnter={onEnter}
                    onMouseMove={onMove}
                    onMouseLeave={onLeave}
                    style={{ cursor: 'pointer' }}
                  />
                )}
              </g>
            );
          })}

          {/* District outline on top so band edges don't bleed past
              the polygon. pointer-events:none so the outline never
              blocks the hover hit-rect underneath. */}
          {MAP_DATA.paths.map((p) => (
            <path
              key={`stroke-${p.id}`}
              d={p.path}
              className="results-map__district-stroke"
              style={{ pointerEvents: 'none' }}
            />
          ))}

          {/* District-number labels last so they're never covered. */}
          <g pointerEvents="none">
            {MAP_DATA.paths.map((p) => (
              <g key={`l${p.id}`}>
                <circle
                  cx={p.labelX}
                  cy={p.labelY}
                  r={15}
                  className="results-map__label-bubble"
                />
                <text
                  x={p.labelX}
                  y={p.labelY}
                  textAnchor="middle"
                  dominantBaseline="central"
                  className="results-map__label-text"
                >
                  {p.id}
                </text>
              </g>
            ))}
          </g>
        </svg>

        {/* Hover tooltip — district name + the 5 seats in election
            order. Fixed-positioned (viewport coords from mouse
            events) so it overlays the SVG without affecting layout;
            pointer-events: none keeps it out of its own hit-test.
            Chroma variant skips the tooltip entirely so it can't
            leak into the broadcast key. */}
        {!isChroma && hover && (() => {
          const ids = byDistrict(hover.districtId).map((c) => c.id);
          const derived = deriveDistrictCount(
            hover.districtId,
            data.districts[String(hover.districtId)],
            ids,
          );
          const path = MAP_DATA.paths.find((p) => p.id === hover.districtId);
          const seats: Array<{
            name: string;
            colour: string;
            chip: string;
            seatNum: number;
          }> = [];
          for (let i = 0; i < SEATS_PER_DISTRICT; i++) {
            const cid = derived.electedSeats[i];
            if (cid) {
              const cand = byId(cid);
              seats.push({
                name: cand?.name ?? cid,
                colour: cand ? partyColour(cand.party) : EMPTY_FILL,
                chip: cand ? partyChip(cand.party) : '',
                seatNum: i + 1,
              });
            } else {
              seats.push({ name: '—', colour: EMPTY_FILL, chip: '', seatNum: i + 1 });
            }
          }
          // Position to the bottom-right of the cursor, but flip to
          // the left if the cursor is near the right viewport edge so
          // the tooltip never overflows.
          const TOOLTIP_W = 260;
          const flipLeft = hover.x + TOOLTIP_W + 24 > window.innerWidth;
          return (
            <div
              role="tooltip"
              className="results-map__tip"
              style={{
                position: 'fixed',
                left: flipLeft ? hover.x - TOOLTIP_W - 12 : hover.x + 12,
                top: hover.y + 12,
                width: TOOLTIP_W,
                pointerEvents: 'none',
                zIndex: 80,
              }}
            >
              <div className="results-map__tip-head">
                District {hover.districtId}
                {path?.properties.name &&
                  path.properties.name !== `District ${hover.districtId}` && (
                    <span className="results-map__tip-subhead">
                      {' · '}
                      {path.properties.name}
                    </span>
                  )}
              </div>
              <ol className="results-map__tip-list">
                {seats.map((s) => (
                  <li
                    key={s.seatNum}
                    className={`results-map__tip-row${s.name === '—' ? ' is-empty' : ''}`}
                  >
                    <span className="results-map__tip-seat">{s.seatNum}.</span>
                    {s.name === '—' ? (
                      <span className="results-map__tip-empty-label">
                        Seat undecided
                      </span>
                    ) : (
                      <>
                        <span
                          className="results-map__tip-swatch"
                          style={{ background: s.colour }}
                        />
                        <span className="results-map__tip-name">{s.name}</span>
                        <span className="results-map__tip-chip">{s.chip}</span>
                      </>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          );
        })()}
      </div>

      {/* Legend — grey "seat not decided" first, then any party that
          has actually won a seat somewhere on the map. Suppressed in
          chroma mode (broadcast overlay shows the map only). */}
      {!isChroma && (
      <div className="results-map__legend" aria-label="Map legend">
        <span className="results-map__legend-item">
          <span
            className="results-map__legend-swatch"
            style={{ background: EMPTY_FILL }}
            aria-hidden="true"
          />
          Seat not yet decided
        </span>
        {legend.map((c) => (
          <span key={c.party} className="results-map__legend-item">
            <span
              className="results-map__legend-swatch"
              style={{ background: partyColour(c.party) }}
              aria-hidden="true"
            />
            {partyChip(c.party)}
          </span>
        ))}
        <p className="results-map__legend-note">
          Five horizontal bands per district = the five seats. Bands fill from
          the bottom in election order.
        </p>
      </div>
      )}
    </section>
  );
}
